type AnyObject = Record<string, unknown>;

type RecursiveOmit<T, O extends string> = T extends (infer U)[]
  ? RecursiveOmit<U, O>[]
  : T extends Record<string, unknown>
    ? { [K in Exclude<keyof T, O>]: RecursiveOmit<T[K], O> }
    : T;

export const recursiveOmit = <T extends AnyObject, O extends string>(
  obj: T,
  keyToRemove: O,
): RecursiveOmit<T, O> => {
  return (Object.keys(obj) as (keyof typeof obj)[]).reduce(
    (acc, key) => {
      if (key === keyToRemove) {
        return acc;
      }

      const value = obj[key];

      if (Array.isArray(value)) {
        return {
          ...acc,
          [key]: value.map((item) => recursiveOmit(item, keyToRemove)),
        };
      }

      if (typeof value === 'object' && value !== null) {
        return {
          ...acc,
          [key]: recursiveOmit(value as AnyObject, keyToRemove),
        };
      }

      return {
        ...acc,
        [key]: value,
      };
    },
    {} as RecursiveOmit<T, O>,
  );
};

export const removeTypenames = <T extends AnyObject>(obj: T) =>
  recursiveOmit(obj, '__typename');
